import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { emailPoToSupplier } from '@/lib/emailPo';
import { useAuthContext } from '@/context/AuthContext';
import { formatCents, todayISO } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchCombobox } from '@/components/SearchCombobox';
import { resolveSqftQuantity, describeSqftQuantity, isAreaUnit } from '@billflow/shared';
import type { Supplier, InventoryItem, SqftMode } from '@billflow/shared';

// ─── Local types ───────────────────────────────────────────────────────────────

interface PoLineRow {
  key: string;
  item_id: number | null;
  description: string;
  qtyStr: string;
  priceStr: string;
  sqft_per_box: number | null;
  note: string;
  // Sq.ft area pricing fields (mirror the estimate form)
  unit: string; // item's UOM ('Sq.ft.' | 'Pcs' | 'Bag' | '')
  entryMode: SqftMode; // 'box' | 'sqft' — how the qty input is interpreted
  sqft_per_piece: number | null;
}

interface SearchItem {
  id: number;
  name: string;
  code: string | null;
  category: string;
}

interface SearchResponse {
  data: { items: SearchItem[] };
}

interface SuppliersResponse {
  data: { suppliers: Supplier[] };
}

interface InventoryItemResponse {
  data: { item: InventoryItem };
}

interface PoDetailResponse {
  data: {
    purchase_order: {
      id: number;
      number: string;
      supplier_id: number;
      order_date: string;
      expected_date: string | null;
      notes: string | null;
      status: string;
    };
    items: Array<{
      id: number;
      item_id: number | null;
      description: string;
      qty: number;
      unit_price: number;
      sqft_per_box: number | null;
      sqft: number | null;
      sort_order: number;
      note: string | null;
      unit: string | null;
    }>;
    supplier: Supplier;
  };
}

interface CreatePoResponse {
  data: { purchase_order: { id: number; number: string } };
}

interface PoPrefillResponse {
  data: {
    suggested_supplier_id: number | null;
    items: Array<{
      item_id: number | null;
      description: string;
      qty: number;
      unit_price: number; // already the purchase cost in cents
      sqft_per_box: number | null;
      sqft: number | null;
      sort_order: number;
    }>;
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

let rowCounter = 0;
function nextKey(): string {
  return `po-row-${++rowCounter}`;
}

function blankRow(): PoLineRow {
  return {
    key: nextKey(),
    item_id: null,
    description: '',
    qtyStr: '1',
    priceStr: '',
    sqft_per_box: null,
    note: '',
    unit: '',
    entryMode: 'box',
    sqft_per_piece: null,
  };
}

// ── effectiveQty ─────────────────────────────────────────────────────────────
// Single source of truth for the resolved quantity used in BOTH the live total
// and the submit payload — ensures they can never diverge. Mirrors the estimate
// form: for Sq.ft. items the resolved AREA is the line qty; otherwise the raw count.
function effectiveQty(row: PoLineRow): number {
  const raw = parseFloat(row.qtyStr) || 0;
  if (!isAreaUnit(row.unit)) return raw;
  return resolveSqftQuantity(raw, row.entryMode, row.sqft_per_box ?? 0, row.sqft_per_piece ?? 0);
}

function computeLineTotal(row: PoLineRow): number {
  // Clamp negatives so a mistyped qty/price never renders a negative live total
  // (the server rejects qty <= 0 on save).
  const qty = Math.max(0, effectiveQty(row));
  const price = Math.max(0, Math.round((parseFloat(row.priceStr) || 0) * 100));
  return Math.round(qty * price);
}

// ─── Supplier Combobox ─────────────────────────────────────────────────────────

interface SupplierComboboxProps {
  value: number | null;
  displayName: string;
  onChange: (id: number, name: string) => void;
  disabled?: boolean;
  /** Called after Tab/Enter selects a supplier — used to focus the first line's item. */
  onConfirmAdvance?: () => void;
}

function SupplierCombobox({
  value,
  displayName,
  onChange,
  disabled,
  onConfirmAdvance,
}: SupplierComboboxProps) {
  // The combobox text mirrors the selected supplier's name when one is chosen, and
  // becomes a free filter query while the user types. `query` is the single source of
  // input text (no read-only "selected" pill mode → no clear-to-search bug). This is
  // a select-only entity combobox: no free text is committed, no "create" footer.
  const [query, setQuery] = useState(displayName);

  // Keep the field text in sync when the parent sets the supplier name externally
  // (e.g. edit-mode prefill, or from-estimate prefill resolving the suggested supplier).
  useEffect(() => {
    setQuery(displayName);
  }, [displayName]);

  // Active suppliers are cached once and filtered locally (preserves the original data
  // source — no per-keystroke server search). Once a supplier is selected (value !== null)
  // the text equals its name and we stop filtering, so a freshly-picked supplier doesn't
  // re-open a dropdown of itself. Typing drops the selection (value → null) and filters.
  const { data, isFetching } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => apiFetch<SuppliersResponse>('/api/suppliers?status=active'),
    staleTime: 30_000,
  });

  const allSuppliers = data?.data.suppliers ?? [];
  const shouldFilter = value === null;
  const suppliers = !shouldFilter
    ? []
    : query.trim()
      ? allSuppliers.filter(
          (s) =>
            s.name.toLowerCase().includes(query.toLowerCase()) ||
            (s.code && s.code.toLowerCase().includes(query.toLowerCase())),
        )
      : allSuppliers;

  function handleSelect(s: Supplier) {
    onChange(s.id, s.name);
    setQuery(s.name);
  }

  function handleQueryChange(q: string) {
    setQuery(q);
    // Typing drops any prior selection so a new one can take its place; clearing the
    // field returns to an empty, ready filter. (Re-search is never blocked.)
    if (value !== null) onChange(0, q);
  }

  return (
    <SearchCombobox<Supplier>
      value={value}
      query={query}
      onQueryChange={handleQueryChange}
      results={suppliers}
      getKey={(s) => s.id}
      renderResult={(s) => (
        <>
          <span className="font-medium flex-1">{s.name}</span>
          {s.code && <span className="text-xs text-muted-foreground font-mono">{s.code}</span>}
        </>
      )}
      onSelect={handleSelect}
      onConfirmAdvance={onConfirmAdvance}
      placeholder="Search suppliers…"
      disabled={disabled}
      loading={isFetching && shouldFilter}
      promptMessage="Type to filter suppliers"
      emptyMessage={value === null ? 'No active suppliers found' : undefined}
    />
  );
}

// ─── Item Search Combobox (per row) ───────────────────────────────────────────

interface ItemComboboxProps {
  value: number | null;
  description: string;
  onSelect: (item: InventoryItem) => void;
  onDescriptionChange: (desc: string) => void;
  disabled?: boolean;
  /** Called with the input element on mount so parent can register it in the focus registry */
  registerInput?: (el: HTMLInputElement | null) => void;
  /** Called after a keyboard Tab-select so parent can focus the Qty input */
  onAfterKeyboardSelect?: () => void;
}

function ItemCombobox({
  value,
  description,
  onSelect,
  onDescriptionChange,
  disabled,
  registerInput,
  onAfterKeyboardSelect,
}: ItemComboboxProps) {
  // The line's `description` IS the combobox text. Typing edits it AND drops any prior
  // item_id (the parent's onDescriptionChange resets item_id to null), so a wrongly-
  // selected item can always be re-searched — this is what fixes the clear-to-search bug.
  // PO lines accept free-text descriptions but cannot create new inventory items here.

  // Once an item is selected (value !== null) the text equals its name; we stop
  // searching so the dropdown doesn't re-open on the just-picked item. Typing resets
  // value to null (via onDescriptionChange in the parent), which re-enables searching.
  const shouldSearch = description.length >= 1 && value === null;

  const { data, isFetching } = useQuery({
    queryKey: ['item-search', description],
    queryFn: () => apiFetch<SearchResponse>(`/api/search?q=${encodeURIComponent(description)}`),
    enabled: shouldSearch,
    staleTime: 10_000,
  });

  const items = shouldSearch ? (data?.data.items ?? []) : [];

  async function handleSelect(si: SearchItem) {
    try {
      const res = await apiFetch<InventoryItemResponse>(`/api/inventory/${si.id}`);
      onSelect(res.data.item);
    } catch {
      toast.error('Failed to load item details');
    }
  }

  // Offer to keep the typed text as a free-text line description (no inventory link).
  const showUseAsDescription =
    !isFetching && description.trim().length >= 1 && value === null && items.length === 0;

  return (
    <SearchCombobox<SearchItem>
      value={value}
      query={description}
      onQueryChange={onDescriptionChange}
      results={items}
      getKey={(si) => si.id}
      renderResult={(si) => (
        <>
          <span className="font-medium flex-1 truncate">{si.name}</span>
          {si.code && <span className="font-mono text-muted-foreground">{si.code}</span>}
        </>
      )}
      onSelect={(si) => handleSelect(si)}
      onConfirmAdvance={onAfterKeyboardSelect}
      registerInput={registerInput}
      allowFreeText
      placeholder="Search or type description…"
      className="h-8 text-xs"
      disabled={disabled}
      loading={isFetching && shouldSearch}
      promptMessage="Type to search inventory"
      footer={(q) =>
        showUseAsDescription ? (
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-muted transition-colors border-t"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDescriptionChange(q);
            }}
          >
            <Plus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">Use &ldquo;</span>
            <span className="font-medium truncate">{q.trim()}</span>
            <span className="text-muted-foreground">&rdquo; as description</span>
          </button>
        ) : null
      }
    />
  );
}

// ─── Line Item Row ─────────────────────────────────────────────────────────────

interface LineRowProps {
  row: PoLineRow;
  index: number;
  onChange: (key: string, patch: Partial<PoLineRow>) => void;
  onRemove: (key: string) => void;
  lineTotal: number;
  disabled?: boolean;
  isLast?: boolean;
  onAddRowAndFocus?: () => void;
  onFocusNextItemSearch?: () => void;
  registerItemInput?: (el: HTMLInputElement | null) => void;
}

function PoLineItemRow({
  row,
  index,
  onChange,
  onRemove,
  lineTotal,
  disabled,
  isLast,
  onAddRowAndFocus,
  onFocusNextItemSearch,
  registerItemInput,
}: LineRowProps) {
  const isSqft = isAreaUnit(row.unit);
  const qtyRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  function handleItemSelect(item: InventoryItem) {
    onChange(row.key, {
      item_id: item.id,
      description: item.name,
      // Buy-side: use purchase_price
      priceStr: String(item.purchase_price / 100),
      sqft_per_box: item.sqft_per_box,
      sqft_per_piece: item.sqft_per_piece ?? null,
      unit: item.unit ?? '',
      entryMode: 'box',
    });
  }

  // A Sq.ft. item only shows the Box/ft² toggle + box/piece breakdown when a box
  // size is configured; without one the qty box IS the area in sq ft (typed directly).
  const hasBoxSize = (row.sqft_per_box ?? 0) > 0;

  // Build resolution caption for Sq.ft lines (only meaningful with a box size)
  const resolvedArea = isSqft ? effectiveQty(row) : null;
  const rawInput = parseFloat(row.qtyStr) || 0;

  let sqftCaption: string | null = null;
  if (isSqft && hasBoxSize && resolvedArea !== null && rawInput > 0) {
    const breakdown = describeSqftQuantity(resolvedArea, row.sqft_per_box ?? 0, row.sqft_per_piece);
    sqftCaption = `→ ${resolvedArea.toFixed(2)} sq ft (${breakdown})`;
  }

  // Qty label hint
  const qtyLabel = isSqft ? (hasBoxSize && row.entryMode === 'box' ? 'boxes' : 'sq ft') : null;

  return (
    <>
      <tr className="hover:bg-muted/20 transition-colors group">
        {/* # */}
        <td className="px-2 py-2 text-xs text-muted-foreground text-center w-7 select-none">
          {index + 1}
        </td>

        {/* Item / Description */}
        <td className="px-1 py-1.5 min-w-[180px]">
          <ItemCombobox
            value={row.item_id}
            description={row.description}
            onSelect={handleItemSelect}
            onDescriptionChange={(desc) => {
              // Typing in the field reverts the row to free-text: keep the text but
              // drop the linked item and its derived (sqft/unit) fields so the
              // dropdown can search again. Price is intentionally preserved so an
              // edited description on a priced line keeps its price.
              if (row.item_id !== null) {
                onChange(row.key, {
                  description: desc,
                  item_id: null,
                  sqft_per_box: null,
                  sqft_per_piece: null,
                  unit: '',
                  entryMode: 'box',
                });
              } else {
                onChange(row.key, { description: desc });
              }
            }}
            disabled={disabled}
            registerInput={registerItemInput}
            onAfterKeyboardSelect={() => qtyRef.current?.focus()}
          />
          {sqftCaption && (
            <p className="mt-0.5 text-[11px] font-mono text-muted-foreground pl-1 leading-tight">
              {sqftCaption}
            </p>
          )}
        </td>

        {/* Box | ft² entry-mode toggle (Sq.ft. items only) — between description and qty */}
        <td className="px-1 py-1.5 w-16 align-middle">
          {isSqft && hasBoxSize && (
            <div
              className="flex items-center rounded-md overflow-hidden border border-input h-5"
              tabIndex={-1}
            >
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => onChange(row.key, { entryMode: 'box' })}
                className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 ${
                  row.entryMode === 'box'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                Box
              </button>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => onChange(row.key, { entryMode: 'sqft' })}
                className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 border-l border-input ${
                  row.entryMode === 'sqft'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                ft²
              </button>
            </div>
          )}
        </td>

        {/* Qty */}
        <td className="px-1 py-1.5 w-20">
          <div className="relative">
            <Input
              ref={qtyRef}
              type="number"
              min="0"
              step="any"
              value={row.qtyStr}
              onChange={(e) => onChange(row.key, { qtyStr: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  priceRef.current?.focus();
                }
              }}
              className="h-8 text-xs text-right tabular-nums"
              disabled={disabled}
            />
            {qtyLabel && (
              <span className="absolute right-1.5 top-full mt-0.5 text-[9px] text-muted-foreground pointer-events-none leading-none whitespace-nowrap">
                {qtyLabel}
              </span>
            )}
          </div>
        </td>

        {/* Unit Price */}
        <td className="px-1 py-1.5 w-28">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              ref={priceRef}
              type="number"
              min="0"
              step="0.01"
              value={row.priceStr}
              onChange={(e) => onChange(row.key, { priceStr: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  if (isLast) {
                    onAddRowAndFocus?.();
                  } else {
                    onFocusNextItemSearch?.();
                  }
                }
              }}
              className="h-8 text-xs text-right pl-5 tabular-nums"
              disabled={disabled}
              placeholder="0.00"
            />
            {isSqft && (
              <span className="absolute right-2 top-full mt-0.5 text-[9px] text-muted-foreground pointer-events-none leading-none whitespace-nowrap">
                /sq.ft
              </span>
            )}
          </div>
        </td>

        {/* Line Total */}
        <td className="px-2 py-1.5 text-right text-xs tabular-nums font-medium w-28">
          {formatCents(lineTotal)}
        </td>

        {/* Remove */}
        <td className="px-1 py-1.5 w-8">
          <button
            type="button"
            onClick={() => onRemove(row.key)}
            disabled={disabled}
            className="opacity-0 group-hover:opacity-100 rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all disabled:pointer-events-none"
            title="Remove row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      <tr className="border-b last:border-0">
        <td />
        <td colSpan={6} className="px-1 pb-1.5 pt-0">
          <input
            type="text"
            value={row.note}
            onChange={(e) => onChange(row.key, { note: e.target.value })}
            disabled={disabled}
            placeholder="Optional remark (shows on the PDF)…"
            className="w-full rounded border-0 border-b border-dashed border-muted-foreground/30 bg-transparent px-1 py-0.5 text-xs italic text-muted-foreground placeholder:text-muted-foreground/40 focus:border-primary/50 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          />
        </td>
      </tr>
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function PurchaseOrderFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // When opened from an estimate (`?from_estimate=:id`), the form pre-fills its
  // rows + supplier and remembers the source estimate to link on save.
  const fromEstimateParam = searchParams.get('from_estimate');
  const fromEstimate = !isEdit && fromEstimateParam ? Number(fromEstimateParam) : null;

  // ── Form state ──
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [supplierName, setSupplierName] = useState('');
  const [orderDate, setOrderDate] = useState(todayISO());
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  // New POs start with 4 blank rows (most orders are a handful of items); edit
  // mode and from-estimate start with one placeholder that the prefill effect
  // replaces with the real lines.
  const [rows, setRows] = useState<PoLineRow[]>(() =>
    isEdit || fromEstimate ? [blankRow()] : Array.from({ length: 4 }, () => blankRow()),
  );
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState<'draft' | 'send' | null>(null);
  // The estimate this PO is created from (sent as source_estimate_id on save).
  const [sourceEstimateId, setSourceEstimateId] = useState<number | null>(fromEstimate);
  const [prefilledFromEstimate, setPrefilledFromEstimate] = useState(false);

  // ── Supplier deposit (NEW POs only) ──
  const [recordPayment, setRecordPayment] = useState(false);
  const [paymentAmountStr, setPaymentAmountStr] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());

  const { user } = useAuthContext();

  // ── Load PO for edit mode ──
  const { data: editData, isLoading: editLoading } = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => apiFetch<PoDetailResponse>(`/api/purchase-orders/${id}`),
    enabled: isEdit,
    staleTime: Infinity,
  });

  // Guard: the edit form is reachable directly by URL, so redirect out when this
  // PO can't actually be edited (admin + draft/sent only — matches the server).
  useEffect(() => {
    if (!isEdit || !editData) return;
    const status = editData.data.purchase_order.status;
    // Operators may edit DRAFT POs; SENT stays admin-only.
    const editable = status === 'draft' || (user?.role === 'admin' && status === 'sent');
    if (!editable) {
      toast.error('This purchase order can no longer be edited.');
      navigate(`/purchase-orders/${id}`, { replace: true });
    }
  }, [isEdit, editData, user, id, navigate]);

  // One-shot prefill guard
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editData || prefilled.current) return;
    prefilled.current = true;
    const po = editData.data.purchase_order;
    const items = editData.data.items;
    const sup = editData.data.supplier;

    setSupplierId(po.supplier_id);
    setSupplierName(sup.name);
    setOrderDate(po.order_date.slice(0, 10));
    setExpectedDate(po.expected_date ? po.expected_date.slice(0, 10) : '');
    setNotes(po.notes ?? '');
    setRows(
      items.map((it) => {
        // Reconstruct unit from the line's item (joined from inventory_items) or default.
        const unit = it.unit ?? '';
        const isSqftLine = isAreaUnit(unit);
        const spb = it.sqft_per_box ?? 0;

        // Reconstruct entryMode and qtyStr from the stored area (it.qty = area for
        // sqft items), mirroring the estimate form's edit-prefill. The PO detail
        // endpoint doesn't return sqft_per_piece, so it's left null (only affects
        // the breakdown caption label, not the resolved area on a whole-box line).
        let entryMode: SqftMode = 'box';
        let qtyStr = String(it.qty);
        if (isSqftLine && spb > 0) {
          const area = it.qty;
          const isWholeBox = spb > 0 && Math.abs(area / spb - Math.round(area / spb)) < 1e-6;
          if (isWholeBox) {
            entryMode = 'box';
            qtyStr = String(Math.round(area / spb));
          } else {
            entryMode = 'sqft';
            qtyStr = String(area);
          }
        }

        return {
          key: nextKey(),
          item_id: it.item_id,
          description: it.description,
          qtyStr,
          priceStr: String(it.unit_price / 100),
          sqft_per_box: it.sqft_per_box,
          sqft_per_piece: null,
          note: it.note ?? '',
          unit,
          entryMode,
        };
      }),
    );
  }, [editData]);

  // ── Prefill from estimate (NEW POs only) ──
  // One-shot guard so a refetch never clobbers in-progress edits.
  const estimatePrefilled = useRef(false);
  useEffect(() => {
    if (!fromEstimate || estimatePrefilled.current) return;
    estimatePrefilled.current = true;

    (async () => {
      try {
        const res = await apiFetch<PoPrefillResponse>(`/api/estimates/${fromEstimate}/po-prefill`);
        const { suggested_supplier_id, items } = res.data;

        const mapped = items.map((it) => {
          // The po-prefill endpoint sends `qty` already as the estimate's resolved
          // value (AREA for sqft items) but no `unit`/`sqft_per_piece`. To display
          // a Box/ft² breakdown without re-bucketing the area (which must stay
          // penny-identical to the estimate), only treat the line as a whole-box
          // Sq.ft. line — that reproduces the exact area in 'box' mode. Any other
          // line keeps unit='' so effectiveQty passes the area through unchanged.
          const spb = it.sqft_per_box ?? 0;
          const area = it.qty;
          const isWholeBox = spb > 0 && Math.abs(area / spb - Math.round(area / spb)) < 1e-6;
          const isSqftLine = it.sqft_per_box != null && isWholeBox;

          return {
            key: nextKey(),
            item_id: it.item_id,
            description: it.description,
            qtyStr: isSqftLine ? String(Math.round(area / spb)) : String(it.qty),
            // unit_price is already the purchase cost in cents.
            priceStr: String(it.unit_price / 100),
            sqft_per_box: it.sqft_per_box,
            sqft_per_piece: null,
            note: '',
            unit: isSqftLine ? 'Sq.ft.' : '',
            entryMode: 'box' as SqftMode,
          };
        });
        // Fall back to a single blank row if the estimate has no goods lines.
        setRows(mapped.length > 0 ? mapped : [blankRow()]);

        if (suggested_supplier_id != null) {
          // Resolve the supplier's name for the combobox display.
          try {
            const sup = await apiFetch<{ data: { supplier: Supplier } }>(
              `/api/suppliers/${suggested_supplier_id}`,
            );
            setSupplierId(suggested_supplier_id);
            setSupplierName(sup.data.supplier.name);
          } catch {
            // Name lookup failed — leave the supplier unselected for the user to pick.
          }
        }
        setPrefilledFromEstimate(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load estimate';
        toast.error(msg);
        // Fall back to a normal blank new PO.
        setSourceEstimateId(null);
        setRows([blankRow()]);
      }
    })();
  }, [fromEstimate]);

  // ── Row mutation helpers ──
  const handleRowChange = useCallback((key: string, patch: Partial<PoLineRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const handleRowRemove = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  // ── Cross-row focus registry ──
  // Maps row.key → the ItemCombobox search input element for that row.
  const itemInputRegistry = useRef<Map<string, HTMLInputElement | null>>(new Map());
  // Stable register callbacks — keyed by row.key so the registration useEffect
  // inside ItemCombobox doesn't re-fire on every parent render.
  const registerCallbackCache = useRef<Map<string, (el: HTMLInputElement | null) => void>>(
    new Map(),
  );
  const makeRegisterInput = useCallback((rowKey: string) => {
    if (!registerCallbackCache.current.has(rowKey)) {
      registerCallbackCache.current.set(rowKey, (el: HTMLInputElement | null) => {
        if (el) {
          itemInputRegistry.current.set(rowKey, el);
        } else {
          itemInputRegistry.current.delete(rowKey);
          // Also clean up the callback itself when the row unmounts.
          registerCallbackCache.current.delete(rowKey);
        }
      });
    }
    return registerCallbackCache.current.get(rowKey)!;
  }, []);
  // When addRow is called via keyboard (Price Tab on last row), we store the new
  // row's key here so a useEffect can focus it once it mounts and registers.
  const pendingFocusKey = useRef<string | null>(null);

  function addRow() {
    const newRow = blankRow();
    pendingFocusKey.current = newRow.key;
    setRows((prev) => [...prev, newRow]);
  }

  // After a keyboard-triggered addRow, focus the new row's item-search input
  // once it has registered itself.
  useEffect(() => {
    const key = pendingFocusKey.current;
    if (!key) return;
    const el = itemInputRegistry.current.get(key);
    if (el) {
      el.focus();
      pendingFocusKey.current = null;
    }
    // Run on every rows change so we catch the frame after the new row mounts.
  }, [rows]);

  // ── Live totals ──
  const lineTotals = rows.map(computeLineTotal);
  const poTotal = lineTotals.reduce((sum, t) => sum + t, 0);

  // A row is "real" if it has an item or a typed description; truly-blank rows
  // (one of the 4 starter rows the user never touched) are dropped before save.
  function isRealRow(r: PoLineRow): boolean {
    return r.item_id != null || r.description.trim().length > 0;
  }

  // Parsed deposit amount in cents (NEW POs only).
  const paymentAmountCents = Math.round((parseFloat(paymentAmountStr) || 0) * 100);

  // ── Validation ──
  function validate(): boolean {
    if (!supplierId) {
      toast.error('Select a supplier');
      return false;
    }
    const filledRows = rows.filter(isRealRow);
    if (filledRows.length === 0) {
      toast.error('Add at least one line item');
      return false;
    }
    for (const r of filledRows) {
      // Validate the resolved area (what's actually saved), not the raw input.
      if (effectiveQty(r) <= 0) {
        toast.error(`Row "${r.description}": qty must be greater than 0`);
        return false;
      }
    }
    return true;
  }

  // ── Build request body ──
  // `action` is only meaningful for NEW POs; edit mode reuses the base fields.
  function buildBody(action?: 'draft' | 'send') {
    const body: Record<string, unknown> = {
      supplier_id: supplierId,
      order_date: orderDate,
      expected_date: expectedDate.trim() || undefined,
      notes: notes.trim() || undefined,
      items: rows.filter(isRealRow).map((r, idx) => {
        // Send the resolved AREA as qty for Sq.ft. items (raw count otherwise) —
        // identical to the estimate form; the route stores qty as-is and computes
        // line_total = qty × unit_price.
        const qty = effectiveQty(r);
        const isSqftLine = isAreaUnit(r.unit);
        return {
          item_id: r.item_id ?? undefined,
          description: r.description.trim(),
          qty,
          unit_price: Math.round((parseFloat(r.priceStr) || 0) * 100),
          sqft_per_box: r.sqft_per_box ?? undefined,
          sqft: isSqftLine ? qty : undefined,
          note: r.note.trim() || undefined,
          sort_order: idx,
        };
      }),
    };

    if (!isEdit) {
      body.status = action === 'send' ? 'sent' : 'draft';
      if (sourceEstimateId != null) {
        body.source_estimate_id = sourceEstimateId;
      }
      // A payment is only allowed when sending (backend 400s on a draft payment).
      if (action === 'send' && recordPayment && paymentAmountCents > 0) {
        body.payment_amount = paymentAmountCents;
        body.payment_date = paymentDate;
      }
    }

    return body;
  }

  // ── Submit (edit mode) ──
  async function handleEditSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await apiFetch(`/api/purchase-orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(buildBody()),
      });
      toast.success('Purchase order updated');
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger'] });
      navigate(`/purchase-orders/${id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Submit (new PO) ──
  async function handleCreate(action: 'draft' | 'send') {
    if (!validate()) return;
    setSubmitting(true);
    setPendingAction(action);
    try {
      const res = await apiFetch<CreatePoResponse>('/api/purchase-orders', {
        method: 'POST',
        body: JSON.stringify(buildBody(action)),
      });
      const poId = res.data.purchase_order.id;
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger'] });
      if (action === 'send') {
        // Marking it sent succeeded — now email the PO PDF to the supplier (warns, never throws).
        await emailPoToSupplier(poId);
      } else {
        toast.success('Purchase order saved as draft');
      }
      navigate(`/purchase-orders/${poId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  }

  // ── Loading state for edit mode ──
  if (isEdit && editLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pageTitle = isEdit
    ? `Edit ${editData?.data.purchase_order.number ?? 'Purchase Order'}`
    : 'New Purchase Order';

  const backTo = isEdit ? `/purchase-orders/${id}` : '/purchase-orders';
  const backLabel = isEdit ? 'Back to PO' : 'Back to Purchase Orders';

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* ── Top bar ── */}
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {backLabel}
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>
      </div>

      {/* ── Pre-filled-from-estimate banner ── */}
      {prefilledFromEstimate && (
        <p className="text-sm text-muted-foreground">
          Pre-filled from estimate — review the lines and supplier, then save.
        </p>
      )}

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left / center: main form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Card: Supplier & Dates */}
            <div className="rounded-lg border bg-card p-4 space-y-4">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Supplier &amp; Dates
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>
                    Supplier <span className="text-destructive">*</span>
                  </Label>
                  <SupplierCombobox
                    value={supplierId}
                    displayName={supplierName}
                    onChange={(sid, sname) => {
                      setSupplierId(sid || null);
                      setSupplierName(sname);
                    }}
                    disabled={submitting}
                    onConfirmAdvance={() => {
                      // After Tab/Enter selects the supplier, jump to the first line's item.
                      const firstKey = rows[0]?.key;
                      if (firstKey) itemInputRegistry.current.get(firstKey)?.focus();
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="po-order-date">Order Date</Label>
                  <Input
                    id="po-order-date"
                    type="date"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="po-expected-date">Expected Date (optional)</Label>
                  <Input
                    id="po-expected-date"
                    type="date"
                    value={expectedDate}
                    onChange={(e) => setExpectedDate(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Card: Line Items */}
            <div className="rounded-lg border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b bg-muted/30">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Line Items
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/20">
                      <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground w-7">
                        #
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                        Item / Description
                      </th>
                      <th className="w-16" />
                      <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-20">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-28">
                        Unit Price
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-28">
                        Total
                      </th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <PoLineItemRow
                        key={row.key}
                        row={row}
                        index={i}
                        onChange={handleRowChange}
                        onRemove={handleRowRemove}
                        lineTotal={lineTotals[i]}
                        disabled={submitting}
                        isLast={i === rows.length - 1}
                        onAddRowAndFocus={addRow}
                        onFocusNextItemSearch={() => {
                          const nextKey = rows[i + 1]?.key;
                          if (nextKey) {
                            itemInputRegistry.current.get(nextKey)?.focus();
                          }
                        }}
                        registerItemInput={makeRegisterInput(row.key)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t bg-muted/10">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRow}
                  disabled={submitting}
                  className="text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Line Item
                </Button>
              </div>
            </div>

            {/* Card: Notes */}
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <Label
                htmlFor="po-notes"
                className="text-sm font-semibold text-muted-foreground uppercase tracking-wide"
              >
                Notes
              </Label>
              <textarea
                id="po-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                disabled={submitting}
                placeholder="Optional notes for this purchase order…"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
          </div>

          {/* Right: Totals + Actions */}
          <div className="space-y-4">
            {/* PO Total */}
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                PO Total
              </h3>
              <div className="space-y-1.5 pt-1">
                {rows
                  .filter((r) => r.description.trim())
                  .map((row, i) => {
                    const total = computeLineTotal(row);
                    if (total === 0) return null;
                    return (
                      <div key={row.key} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground truncate max-w-[140px]">
                          {row.description || `Row ${i + 1}`}
                        </span>
                        <span className="tabular-nums font-mono">{formatCents(total)}</span>
                      </div>
                    );
                  })}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="font-semibold text-sm">Total</span>
                  <span className="tabular-nums font-semibold text-base font-mono">
                    {formatCents(poTotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Supplier deposit card (NEW POs only) — mirrors the estimate payment card */}
            {!isEdit && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    id="po-record-payment"
                    type="checkbox"
                    checked={recordPayment}
                    onChange={(e) => {
                      setRecordPayment(e.target.checked);
                      // Pre-fill the amount with the full PO total when first enabled.
                      if (e.target.checked && !paymentAmountStr) {
                        setPaymentAmountStr((poTotal / 100).toFixed(2));
                      }
                    }}
                    className="rounded border-gray-300"
                    disabled={submitting}
                  />
                  <Label htmlFor="po-record-payment" className="text-sm cursor-pointer">
                    Record a payment to the supplier now?
                  </Label>
                </div>
                {recordPayment && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1.5">
                      <Label htmlFor="po-payment-amount" className="text-xs">
                        Amount ($)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                          $
                        </span>
                        <Input
                          id="po-payment-amount"
                          type="number"
                          min="0"
                          step="0.01"
                          value={paymentAmountStr}
                          onChange={(e) => setPaymentAmountStr(e.target.value)}
                          className="pl-5 h-8 text-xs tabular-nums"
                          placeholder={String((poTotal / 100).toFixed(2))}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="po-payment-date" className="text-xs">
                        Date
                      </Label>
                      <Input
                        id="po-payment-date"
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="h-8 text-xs"
                        disabled={submitting}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {isEdit ? (
              <div className="space-y-2">
                <Button className="w-full" onClick={handleEditSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
                <Link to={backTo} className="block">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCreate('draft')}
                  disabled={submitting || recordPayment}
                >
                  {submitting && pendingAction === 'draft' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save as Draft'
                  )}
                </Button>
                {recordPayment && (
                  <p className="-mt-1 text-center text-[11px] text-muted-foreground">
                    Uncheck “Record a payment” to save as a draft.
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={() => handleCreate('send')}
                  disabled={submitting || (recordPayment && !(paymentAmountCents > 0))}
                >
                  {submitting && pendingAction === 'send' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : recordPayment ? (
                    `Send & Pay ${formatCents(paymentAmountCents)}`
                  ) : (
                    'Save & Send'
                  )}
                </Button>
                <Link to={backTo} className="block">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
